import { uploadOfflineImageAttachments, filterRemoteUrls } from '../uploadAttachments';
import { deepCopy } from '../utils';

const isRemoteUrl = (url) => typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));

/**
 * Offline operation config for createMedia mutation.
 *
 * Handles:
 * - Queuing media/photo creation when offline
 * - Storing local image URI as attachment
 * - Uploading image to S3 when back online before creating media record
 * - Associating media with machines and service dockets
 */
const createMediaConfig = {
	queueWhenAttachments: true,
	queueOnNetworkError: true,
	serializeVariables: ({ variables }) => {
		return deepCopy(variables);
	},
	createMeta: async ({ variables, helpers, context = {} }) => {
		const input = variables?.input || {};
		const getEndpoint = helpers?.getServerEndpoint;
		let serverEndpoint = null;

		const offlineMeta = context.offlineMeta || {};
		const machine = offlineMeta.machineDetails || input.machineDetails || null;
		const fallbackMachineName = offlineMeta.machineName || input.machineName || null;

		if (getEndpoint) {
			try {
				serverEndpoint = await getEndpoint();
			}
			catch (error) {
				console.log('createMedia offline meta endpoint error', error);
			}
		}

		return {
			machineId: input.machine || offlineMeta.machineId || null,
			serviceDocketId: input.serviceDocket || null,
			localLink: !isRemoteUrl(input.link) ? input.link : null,
			serverEndpoint,
			s3Path: 'images/machines/',
			machine,
			fallbackMachineName,
			type: 'createMedia',
		};
	},
	extractAttachments: ({ variables }) => {
		const input = variables?.input || {};
		if (!input.link || isRemoteUrl(input.link)) {
			return [];
		}

		return [{
			type: 'image',
			localUri: input.link,
		}];
	},
	prepareVariables: async ({ entry }) => {
		const clonedVariables = deepCopy(entry.variables || {});
		const input = clonedVariables.input || {};
		const attachments = entry.attachments || [];

		if (attachments.length) {
			const uploaded = await uploadOfflineImageAttachments({ attachments, meta: entry.meta || {} });
			const uploadedLink = uploaded.length ? uploaded[0].remoteUrl : null;
			if (uploadedLink) {
				input.link = uploadedLink;
			}
		}
		else {
			const [remoteLink] = filterRemoteUrls([input.link]);
			if (remoteLink) {
				input.link = remoteLink;
			}
		}

		clonedVariables.input = input;
		return clonedVariables;
	},
	onSuccess: ({ entry, client }) => {
		const serviceDocketId = entry.meta?.serviceDocketId;
		if (serviceDocketId) {
			client.cache.modify({
				id: client.cache.identify({ __typename: 'ServiceDocket', id: serviceDocketId }),
				fields: { photoTaken: () => true },
			});
		}
	},
	getOfflineResult: ({ queueEntry, variables }) => {
		const meta = queueEntry?.meta || {};
		const input = variables?.input || {};
		const link = meta.localLink || input.link || '';

		return {
			createMedia: {
				__typename: 'Media',
				id: queueEntry?.id,
				link,
				machine: meta.machine || null,
				serviceDocket: meta.serviceDocketId ? {
					__typename: 'ServiceDocket',
					id: meta.serviceDocketId,
					machine: meta.machine || null,
				} : null,
				offline: true,
			},
		};
	},
};

export default createMediaConfig;
